"use client";

import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  Banknote,
  Key,
  UserSquare,
  Cloud,
  ClipboardList,
  Columns3,
  CreditCard,
  GraduationCap,
  Layers3,
  Menu,
  Plug,
  FileCheck2,
} from "lucide-react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import Image from "next/image";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@workspace/ui/components/dialog";

import { SidebarItem } from "./sidebar-item";
import { useOptionalSidebarVisibility } from "./sidebar-visibility-provider";

const SidebarUserFooter = dynamic(
  () =>
    import("./sidebar-user-footer").then((mod) => ({
      default: mod.SidebarUserFooter,
    })),
  { ssr: false },
);

export default function SideBar() {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const sidebarVisibility = useOptionalSidebarVisibility();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const navItems = [
    {
      icon: LayoutDashboard,
      label: "Dashboard",
      path: "/dashboard",
    },
    { icon: Columns3, label: "CRM", path: "/crm" },
    { icon: Users, label: "Clientes", path: "/customers" },
    { icon: Users, label: "Usuários", path: "/users" },
    {
      icon: Banknote,
      label: "Propostas de Venda",
      path: "/proposals",
    },
    { icon: Banknote, label: "Vendas", path: "/sales" },
    { icon: Key, label: "Licenças", path: "/license-items" },
    {
      icon: GraduationCap,
      label: "Licenças educacionais",
      path: "/educational-license-pools",
    },
    { icon: UserSquare, label: "Representantes", path: "/dealers" },
    { icon: Cloud, label: "Downloads", path: "/downloads" },
    { icon: FileCheck2, label: "Termos de Uso", path: "/terms-of-use" },
    { icon: Layers3, label: "Planos", path: "/plans" },
    { icon: CreditCard, label: "Assinaturas", path: "/subscriptions" },
    { icon: Plug, label: "Integrações", path: "/integrations" },
    {
      icon: ClipboardList,
      label: "Logs de Trial",
      path: "/trial-logs",
    },
  ];

  return (
    <>
      <div className="fixed left-3 top-3 z-40 lg:hidden">
        <Button
          type="button"
          variant="outline"
          className="h-9 w-9 p-0 bg-background/90 backdrop-blur"
          aria-label="Abrir menu"
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <Menu size={18} />
        </Button>
      </div>

      <Dialog open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <DialogContent
          className="left-0 top-0 h-screen w-[86vw] max-w-[320px] translate-x-0 translate-y-0 rounded-none border-r border-border p-0"
          showCloseButton={false}
          overlayClassName="bg-foreground/60"
        >
          <DialogTitle className="sr-only">Menu</DialogTitle>
          <aside className="flex h-full w-full flex-col bg-sidebar">
            <div className="flex h-16 shrink-0 items-center border-b border-border px-4">
              <div className="flex min-w-0 items-center gap-3">
                <Image
                  src="/logo-platform.svg"
                  alt="GeoOffice Admin"
                  width={28}
                  height={28}
                  className="size-7 shrink-0"
                  priority
                />
                <div className="min-w-0">
                  <h1 className="truncate text-base font-semibold text-foreground">
                    GeoOffice Admin
                  </h1>
                </div>
              </div>
            </div>

            <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
              {navItems.map((item) => (
                <SidebarItem
                  key={`mobile-${item.path}`}
                  icon={item.icon}
                  label={item.label}
                  href={item.path}
                  active={
                    item.path === "/crm" ? pathname.endsWith("/crm") : undefined
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                />
              ))}
            </nav>

            <SidebarUserFooter />
          </aside>
        </DialogContent>
      </Dialog>

      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col overflow-hidden bg-sidebar transition-[width,border] duration-200 lg:flex",
          sidebarVisibility?.isSidebarCollapsed
            ? "w-0 border-r-0"
            : "w-64 border-r border-border",
        )}
        aria-hidden={sidebarVisibility?.isSidebarCollapsed}
      >
        <div className="flex h-full w-64 shrink-0 flex-col">
          <div className="flex h-16 shrink-0 items-center border-b border-border px-4">
            <div className="flex min-w-0 items-center gap-3">
              <Image
                src="/logo-platform.svg"
                alt="GeoOffice Admin"
                width={28}
                height={28}
                className="size-7 shrink-0"
                priority
              />
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold text-foreground">
                  GeoOffice Admin
                </h1>
              </div>
            </div>
          </div>

          <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
            {navItems.map((item) => (
              <SidebarItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                href={item.path}
                active={
                  item.path === "/crm" ? pathname.endsWith("/crm") : undefined
                }
              />
            ))}
          </nav>

          <SidebarUserFooter />
        </div>
      </aside>
    </>
  );
}
